import type { ReactElement } from 'react';
import type { AgentmeshScanCoverage } from '@kirin/api-client/contracts/extensions';
import { cn } from '@/lib/utils';
import { LabelLessState } from './LabelLessState';
import {
  NOT_APPLICABLE_DESCRIPTION,
  NOT_APPLICABLE_LABEL,
  SCAN_STATUS_LABELS,
  scanStatusOf,
} from './agentmesh-risk-labels';

/**
 * Pill per coverage, in AgentMesh's own Scan status colours (KIR-5273):
 * Scanned solid blue, Partially scanned a light blue wash, Unscanned grey —
 * a scale of completeness, kept apart from the Risk column's verdict colours
 * — and Manual review purple (an AgentMesh admin set the verdict).
 */
const COVERAGE_CLASSES: Record<AgentmeshScanCoverage, string> = {
  scanned: 'bg-blue-600 text-white dark:bg-blue-500',
  partially_scanned:
    'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  unscanned:
    'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  manual_review:
    'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
};

interface ScanStatusBadgeProps {
  coverage: AgentmeshScanCoverage;
}

/** One coverage as a pill. */
export function ScanStatusBadge({
  coverage,
}: ScanStatusBadgeProps): ReactElement {
  return (
    <span
      data-scan-status={coverage}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap',
        COVERAGE_CLASSES[coverage]
      )}
    >
      {SCAN_STATUS_LABELS[coverage]}
    </span>
  );
}

interface ScanStatusCellProps {
  /** Coverage from the API; null/undefined = none (built-in, or no result). */
  scanStatus: AgentmeshScanCoverage | null | undefined;
  /** A built-in without a coverage reads "Not applicable" (KIR-4726, KIR-4830). */
  isBuiltIn?: boolean;
  /**
   * Explain "Not applicable" on hover and to screen readers. Off where the
   * explanation is already on screen (the expanded row).
   */
  describe?: boolean;
}

/**
 * Scan status cell for inventory extension rows (KIR-5208): how much of the
 * extension AgentMesh scanned. On a collapsed row: Manual review when an
 * AgentMesh admin set every installed version's verdict, Scanned when every
 * version has a full scan or a manual review, Partially scanned when some
 * have a result, Unscanned when none has. A built-in without a verdict
 * reads Not applicable.
 */
export function ScanStatusCell({
  scanStatus,
  isBuiltIn = false,
  describe = true,
}: ScanStatusCellProps): ReactElement {
  const status = scanStatusOf(scanStatus, isBuiltIn);
  if (status === 'not-applicable') {
    return (
      <LabelLessState
        label={NOT_APPLICABLE_LABEL}
        description={NOT_APPLICABLE_DESCRIPTION}
        describe={describe}
      />
    );
  }
  return <ScanStatusBadge coverage={status} />;
}

ScanStatusCell.displayName = 'ScanStatusCell';
